import React, { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { OwnedInvestment, ProjectStatus } from 'api-client';
import { CrossIcon } from 'components/common/icons';
import { ProjectStatusBadge } from 'components/common/StatusBadge';
import { Money, Skeleton } from 'components/common/utils';
import { RouteDictionary } from 'utils';
import { cn } from 'utils';

import styles from '../AccountDetails.module.scss';
import {
  accountInvestmentStatusFilters,
  areStatusFiltersEqual,
} from './accountInvestmentStatusFilters';
import { mapOwnedInvestmentToRow } from './ownedInvestmentRows';

const TABLE_SKELETON_ROWS = 5;

type Props = {
  investments: OwnedInvestment[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  statusFilter: ProjectStatus[];
  onStatusFilterChange: (statuses: ProjectStatus[]) => void;
  onLoadMore: () => void;
  emptyMessage: string;
};

export const MainAccountInvestmentsTable: FC<Props> = ({
  investments,
  loading,
  loadingMore,
  hasMore,
  statusFilter,
  onStatusFilterChange,
  onLoadMore,
  emptyMessage,
}) => {
  const navigate = useNavigate();

  const rows = useMemo(
    () => investments.map((investment) => mapOwnedInvestmentToRow(investment)),
    [investments]
  );

  return (
    <>
      <div className={styles.filtersBar}>
        <div className={styles.statusFilters}>
          {accountInvestmentStatusFilters.map((filter) => {
            const isActive = areStatusFiltersEqual(statusFilter, filter.statuses);

            const handleClick = () => {
              if (loading) {
                return;
              }

              if (isActive && filter.statuses.length > 0) {
                onStatusFilterChange([]);
                return;
              }

              if (!isActive) {
                onStatusFilterChange(filter.statuses);
              }
            };

            return (
              <button
                key={filter.label}
                type="button"
                className={cn(
                  styles.statusFilterButton,
                  isActive && styles.statusFilterButtonActive
                )}
                onClick={handleClick}
                disabled={loading}
                aria-pressed={isActive}
              >
                <span>{filter.label}</span>
                {isActive ? (
                  <CrossIcon
                    preset="circle"
                    size={16}
                    title="Сбросить фильтр"
                    className={styles.statusFilterClearIcon}
                    style={{ color: 'currentColor' }}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.tableCard}>
        <div className={cn(styles.tableHeader, styles.comfortTableHeader)}>
          <div className={styles.th}>ПРОЕКТ</div>
          <div className={styles.th}>СТАТУС</div>
          <div className={styles.th}>ДАТА ВХОДА</div>
          <div className={styles.th}>ИНВЕСТИРОВАНО</div>
          <div className={styles.th}>ВОЗВРАЩЕНО</div>
          <div className={styles.th}>ПРОГНОЗ ВОЗВРАТА</div>
          <div className={styles.th}>ROI / IRR</div>
        </div>

        <div className={styles.tableBody}>
          {loading ? (
            <div className={styles.tableSkeleton} aria-busy="true" aria-live="polite">
              {Array.from({ length: TABLE_SKELETON_ROWS }).map((_, index) => (
                <Skeleton
                  key={index}
                  className={styles.tableSkeletonRow}
                  height={72}
                  borderRadius={0}
                />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className={styles.tableEmpty}>{emptyMessage}</div>
          ) : (
            rows.map((row) => (
              <button
                key={row.key}
                type="button"
                className={cn(styles.tableRow, styles.comfortTableRow, styles.clickableTableRow)}
                onClick={() =>
                  navigate(`/dashboard/${RouteDictionary.DETAILED_PROJECT}/${row.key}`)
                }
              >
                <div className={cn(styles.td, styles.tdProject)}>
                  <div className={styles.projectName}>{row.name}</div>
                  <div className={styles.projectId}>ID {row.projectId}</div>
                </div>
                <div className={cn(styles.td, styles.tdStatus)} data-label="Статус">
                  <ProjectStatusBadge status={row.status} />
                </div>
                <div className={styles.td} data-label="Дата входа">
                  {row.entryDate}
                </div>
                <div className={styles.td} data-label="Инвестировано">
                  <Money money={row.invested} />
                </div>
                <div className={styles.td} data-label="Возвращено">
                  {row.returned ? <Money money={row.returned} /> : '—'}
                </div>
                <div className={styles.td} data-label="Прогноз возврата">
                  {row.forecastReturn ? <Money money={row.forecastReturn} /> : '—'}
                </div>
                <div className={styles.td} data-label="ROI / IRR">
                  {row.roiIrr}
                </div>
              </button>
            ))
          )}
          {loadingMore ? (
            <div className={styles.tableSkeleton}>
              {Array.from({ length: 2 }).map((_, index) => (
                <Skeleton
                  key={`loading-more-${index}`}
                  className={styles.tableSkeletonRow}
                  height={72}
                  borderRadius={0}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {hasMore ? (
        <button
          type="button"
          className={styles.showAllButton}
          onClick={onLoadMore}
          disabled={loadingMore}
        >
          Показать все проекты
        </button>
      ) : null}
    </>
  );
};
